import type {
  ProductReviews,
  StorefrontCategory,
  StorefrontProduct,
  StorefrontProductSummary,
  StorefrontReview,
} from '@/features/storefront/api/storefront';
import { NO_PROFILE_FACTS, type StorefrontTeam } from '@/features/storefront/api/team';
import { placeholderImage } from '@/features/storefront/sections/placeholderArt';
import { heroImages, type FeatureIcon, type Section } from '@/features/storefront/sections/schema';

/**
 * What a section shows in the customiser before the merchant has filled it in.
 *
 * **Why this is content and not a component.** The obvious way to build "show
 * a picture of what a testimonials section looks like" is a `TestimonialsHint`
 * that draws three cards. It is also the wrong way, and it is wrong in a way
 * that only shows up after it ships: the hint would draw *its* three cards, at
 * *its* column count, in *its* alignment - and the merchant would set the
 * section to two columns, centred, on the surface colour, and watch nothing
 * change. A stand-in that ignores the settings beside it is worse than an empty
 * band, because the empty band at least did not lie.
 *
 * So nothing here renders. Each function fills in the *content* a section is
 * missing - the pictures, the quotes, the questions - and hands the section
 * back to the renderer the shopper's storefront uses. Every setting the
 * merchant has touched then applies to the stand-in exactly as it will apply to
 * the real thing, because it is the same code path: columns, alignment, image
 * side, padding, background, typography, rounding, the lot. Change the column
 * count and the placeholder reflows.
 *
 * **The one rule.** This module is reached only from the customiser and the
 * content-page editor, through `SectionEnvironment.placeholders`, which is off
 * by default and which the shopper's storefront never turns on. A placeholder
 * review on a live shop is a fabricated customer quote, so the flag is opt-in
 * rather than opt-out and `SectionPlaceholders.test.tsx` holds it that way.
 *
 * **Why the copy names itself.** The pictures are polished; the words are
 * plainly examples. That pairing is deliberate. A merchant has to be able to
 * see the shape of their page at a glance and, at the same moment, never be in
 * any doubt about which parts of it they still have to write.
 */

/**
 * The mark that says a block was invented here.
 *
 * Placeholder blocks are rendered but not editable - they are not in the
 * merchant's document, so there is no row in the tree to select and no
 * settings to open. The builder reads this to leave them out of its selection
 * frames, which is what stops a click on a stand-in review from selecting a
 * block that does not exist.
 */
const PLACEHOLDER_PREFIX = 'placeholder:';

export function isPlaceholderId(id: string): boolean {
  return id.startsWith(PLACEHOLDER_PREFIX);
}

function placeholderId(kind: string, index: number): string {
  return `${PLACEHOLDER_PREFIX}${kind}-${index}`;
}

// --- The catalog ----------------------------------------------------------
/**
 * Products for a grid the merchant's own catalog cannot fill yet.
 *
 * One name repeated rather than a row of invented brands: a grid of eight
 * plausible-sounding products is a grid a merchant has to look twice at to
 * work out is not theirs, and the whole value of the stand-in is that they
 * never have to. The prices and the pictures vary so the row still reads as a
 * shop rather than as a rendering fault.
 */
export function placeholderProducts(count: number): StorefrontProductSummary[] {
  return Array.from({ length: Math.max(count, 0) }, (_, index) => ({
    id: placeholderId('product', index),
    name: 'Example product title',
    slug: `example-product-${index + 1}`,
    short_description: null,
    price: PRICES[index % PRICES.length],
    compare_at_price: index % 3 === 1 ? '2499.00' : null,
    image_url: placeholderImage('product', index),
    in_stock: true,
    metafields: [],
    // Stars under every other card, so a merchant sees what a rated card
    // looks like beside one nobody has reviewed yet.
    rating_average: index % 2 === 0 ? 4.5 : null,
    rating_count: index % 2 === 0 ? 12 : 0,
  }));
}

const PRICES = ['1499.00', '2199.00', '899.00', '3450.00', '1250.00', '1899.00'];

/**
 * A product's reviews, for the reviews section in the customiser.
 *
 * Content rather than markup, like everything else here: the section renders
 * these through the shopper's own code, so its layout, its page size and its
 * breakdown switch all apply to them. The words say what they are - a review
 * a customer writes appears here - because the one thing a stand-in review
 * must never be mistaken for is a real one.
 */
export function placeholderReviews(pageSize: number): ProductReviews {
  const ratings = [5, 4, 5, 3, 5, 4, 5, 2];
  const items: StorefrontReview[] = ratings.map((rating, index) => ({
    id: placeholderId('review', index),
    rating,
    title: index % 3 === 2 ? null : 'Example review title',
    body:
      'A customer’s review of this product appears here - what they bought, how it arrived and what' +
      ' they thought of it. Reviews wait for your approval before shoppers see them.',
    author_name: 'Customer name',
    verified_purchase: index % 4 !== 3,
    photos: index === 1 ? [placeholderImage('product', 1), placeholderImage('product', 2)] : [],
    merchant_reply: index === 0 ? 'Your reply to a review appears under it, like this.' : null,
    merchant_reply_at: null,
    created_at: '2026-01-01T00:00:00Z',
    updated_at: '2026-01-01T00:00:00Z',
  }));
  const distribution: Record<string, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const rating of ratings) distribution[rating] += 1;
  return {
    summary: {
      average: Math.round((ratings.reduce((sum, each) => sum + each, 0) / ratings.length) * 10) / 10,
      count: ratings.length,
      distribution,
    },
    items: items.slice(0, pageSize),
    total: items.length,
    page: 1,
    page_size: pageSize,
  };
}

/** Shelves for a category grid on a shop that has none yet. */
export function placeholderCategories(count: number): StorefrontCategory[] {
  return Array.from({ length: Math.max(count, 0) }, (_, index) => ({
    id: placeholderId('category', index),
    name: 'Example collection',
    slug: `example-collection-${index + 1}`,
    description: null,
    image_url: placeholderImage('collection', index),
    parent_id: null,
    position: index,
  }));
}

/**
 * People for a team section on a shop that has added nobody yet.
 *
 * Drawn silhouettes and a name that says it is one, never a face: a stand-in
 * person is the clearest case of a placeholder that must not be mistaken for
 * somebody real. The specialities, years, visit types, languages and working
 * days vary so the filters have something to show when the merchant switches
 * them on.
 */
export function placeholderTeam(count: number): StorefrontTeam {
  const specialities = ['Speciality one', 'Speciality two', 'Speciality three'];
  return {
    singular: 'Team member',
    plural: 'Team',
    members: Array.from({ length: Math.max(count, 0) }, (_, index) => ({
      id: placeholderId('team-member', index),
      slug: `example-person-${index + 1}`,
      name: 'Example name',
      photo_url: placeholderImage('portrait', index),
      photo_focal: null,
      role: 'Their role',
      speciality: specialities[index % specialities.length],
      qualifications: null,
      short_bio: 'A line about what they do and who they help.',
      bio: null,
      service_ids: [],
      hours: null,
      fee_note: null,
      ...NO_PROFILE_FACTS,
      // Enough facts for the other filters to draw in the customiser too -
      // never a gender, which is not something to invent about a stand-in.
      experience_years: [6, 12, 22][index % 3],
      visit_formats: index % 2 === 0 ? ['in_person'] : ['in_person', 'online'],
      languages: index % 2 === 0 ? ['English', 'Hindi'] : ['English'],
      available_days: index % 2 === 0 ? [0, 1, 2, 3, 4] : [1, 3, 5],
    })),
  };
}

/**
 * One product in full, for the product page's components.
 *
 * Everything a component on that page can draw from, so that arranging the page
 * before publishing a product shows the arrangement rather than a column of
 * gaps: four pictures for the gallery, two option axes for the picker - one of
 * them named in colour words, so the swatches appear - a compare-at price for
 * the reduction, and a description long enough that the tabs under it have
 * something to hold.
 */
export const PLACEHOLDER_PRODUCT: StorefrontProduct = {
  id: `${PLACEHOLDER_PREFIX}product-detail`,
  name: 'Your product name',
  slug: 'example-product',
  short_description: 'A line about what it is, who it is for, and why it is worth the money.',
  description:
    '<p>The long description you write for this product appears here - what it is made of,' +
    ' how it is made, and what it is like to live with.</p>' +
    '<p>Shoppers read this last and buy on it, so it is worth the paragraph.</p>',
  price: '1499.00',
  compare_at_price: '1999.00',
  brand: 'Your brand',
  images: Array.from({ length: 4 }, (_, index) => ({
    id: placeholderId('product-image', index),
    url: placeholderImage('product', index),
    alt_text: 'Example product photograph',
  })),
  variants: [
    {
      id: placeholderId('variant', 0),
      options: { Colour: 'Sage', Size: 'Medium' },
      price: '1499.00',
      in_stock: true,
      sku: 'EXAMPLE-SG-M',
    },
    {
      id: placeholderId('variant', 1),
      options: { Colour: 'Sand', Size: 'Medium' },
      price: '1499.00',
      in_stock: true,
      sku: 'EXAMPLE-SD-M',
    },
    {
      id: placeholderId('variant', 2),
      options: { Colour: 'Sage', Size: 'Large' },
      price: '1699.00',
      in_stock: true,
      sku: 'EXAMPLE-SG-L',
    },
  ],
  // Enough structured facts that the specifications table, and the theme
  // accordions built on the same fields, draw a table rather than nothing.
  metafields: [
    {
      key: 'material',
      name: 'Material',
      field_type: 'single_line_text',
      is_list: false,
      value: 'The fact, and what you call it',
      entries: [],
    },
    {
      key: 'dimensions',
      name: 'Dimensions',
      field_type: 'single_line_text',
      is_list: false,
      value: '40 × 30 × 12 cm',
      entries: [],
    },
    {
      key: 'care',
      name: 'Care',
      field_type: 'multi_line_text',
      is_list: false,
      value: 'A line per instruction.\nThey are drawn as a list.',
      entries: [],
    },
  ],
  // So the stars beside the price are part of the arrangement being judged.
  rating_average: 4.5,
  rating_count: 12,
};

// --- Sections -------------------------------------------------------------
const TESTIMONIALS: readonly { quote: string; author: string; role: string }[] = [
  {
    quote:
      'Exactly what a review from one of your customers will look like here - a couple of' +
      ' sentences, in their words, about what changed for them.',
    author: 'Example customer',
    role: 'Verified buyer',
  },
  {
    quote:
      'Reviews carry more weight when they are specific. Quote the line that said what your' +
      ' product actually did, not the one that said it was nice.',
    author: 'Example customer',
    role: 'Bought twice',
  },
  {
    quote:
      'Ordered on a Tuesday and it was here on the Thursday. This is where the detail a' +
      ' shopper was worried about gets answered by somebody other than you.',
    author: 'Example customer',
    role: 'Verified buyer',
  },
];

const QUESTIONS: readonly { question: string; answer: string }[] = [
  {
    question: 'How long does delivery take?',
    answer:
      'Answer the question a shopper is holding their card over. This is where your real' +
      ' delivery times, areas and charges go.',
  },
  {
    question: 'Can I return it if it is not right?',
    answer:
      'Say what your returns window is and who pays the postage. A clear answer here sells' +
      ' more than a careful one.',
  },
  {
    question: 'Do you ship outside the country?',
    answer: 'Replace this with the questions your shoppers actually email you about.',
  },
];

const PROMISES: readonly { icon: FeatureIcon; title: string; body: string }[] = [
  { icon: 'truck', title: 'Free delivery', body: 'On every order over ₹999.' },
  { icon: 'return', title: 'Easy returns', body: 'Thirty days, no questions asked.' },
  { icon: 'shield', title: 'Secure payment', body: 'Every card and wallet, protected.' },
  { icon: 'support', title: 'Here to help', body: 'Real people, seven days a week.' },
  { icon: 'leaf', title: 'Made to last', body: 'The promise your shop is built on.' },
  { icon: 'award', title: 'Two-year warranty', body: 'Say what you stand behind.' },
];

/**
 * Figures for a features section in the `stats` style. Round, modest numbers
 * with a line under each that says it is the merchant's to replace: a stand-in
 * "10k" is a shape, and the line keeps it from reading as a claim.
 */
const STATS: readonly { icon: FeatureIcon; stat: string; title: string; body: string }[] = [
  { icon: 'award', stat: '10+', title: 'Years of experience', body: 'Your own figure goes here.' },
  { icon: 'heart', stat: '5k', title: 'Happy customers', body: 'Count the people you have served.' },
  { icon: 'sparkles', stat: '4.9', title: 'Average rating', body: 'From your real reviews.' },
  { icon: 'support', stat: '24/7', title: 'Here for you', body: 'Or the hours you keep.' },
  { icon: 'shield', stat: '100%', title: 'Something you guarantee', body: 'Say what you stand behind.' },
];

/**
 * Steps of a story nobody has written yet. Dated "Year" rather than with a
 * year, so the stand-in never says when the merchant's shop opened.
 */
const MILESTONES: readonly { date: string; title: string; body: string }[] = [
  { date: 'Year', title: 'Where it began', body: 'How and why you started - the first shop, the first customer.' },
  { date: 'Year', title: 'A step forward', body: 'A milestone you are proud of: a new place, a new service.' },
  { date: 'Today', title: 'Where we are now', body: 'What you offer today, and who you offer it to.' },
];

const SLIDES: readonly { heading: string; subheading: string }[] = [
  { heading: 'Your first slide', subheading: 'A line of copy, and the button it leads to.' },
  { heading: 'Your second slide', subheading: 'Rotate a campaign without rebuilding the page.' },
];

const GALLERY_CAPTIONS: readonly string[] = [
  'A caption, if you want one',
  '',
  'Tag your own photographs',
  '',
  '',
  'Shot by a customer',
];

const SERVICES: readonly { name: string; description: string; duration: number; price: number }[] = [
  { name: 'Example service', description: 'What it is, and who books it.', duration: 30, price: 500 },
  { name: 'Example consultation', description: 'A longer appointment.', duration: 60, price: 1200 },
  { name: 'Example follow-up', description: 'For customers who have been before.', duration: 20, price: 400 },
];

const RICH_TEXT =
  '<p>This is where your own writing goes - the story behind the shop, the promise you make' +
  ' to a customer, or whatever this page is for.</p>' +
  '<p>Two or three short paragraphs read better on a storefront than one long one, and the' +
  ' words a shopper actually finishes are the ones that sell.</p>';

/** How many stand-in blocks a grid of this many columns wants. */
function fill(columns: number, rows = 1): number {
  return Math.max(Math.min(columns, 6), 1) * rows;
}

/**
 * Two ways a section is short of content, and they are not the same thing.
 *
 * A section with *no blocks* has nothing in the tree either - there is no row
 * to select and nothing to open - so a set of stand-ins is invented for it,
 * carrying placeholder ids the builder knows to leave unselectable.
 *
 * A section whose blocks exist but are *blank* - the empty review a merchant
 * added and has not written yet - is different: those rows are in the tree,
 * they are the merchant's, and they are what will hold the real words. Those
 * are filled in place, keeping their own ids, so clicking the stand-in in the
 * preview still opens the block it belongs to.
 */
function blankBlocks<T extends { id: string; visible: boolean }>(
  blocks: readonly T[],
  count: number,
  invent: (index: number) => T,
  fillIn: (block: T, index: number) => T,
): T[] | null {
  if (blocks.length === 0) return Array.from({ length: count }, (_, index) => invent(index));

  let touched = false;
  const next = blocks.map((block, index) => {
    const filled = fillIn(block, index);
    if (filled !== block) touched = true;
    return filled;
  });
  return touched ? next : null;
}

/**
 * The same section, with stand-in content wherever the merchant has none.
 *
 * Returns the section it was given, unchanged and by identity, whenever there
 * is nothing to fill - which is the common case on a shop that has been set up,
 * and which is what keeps the preview from re-rendering the world on every
 * keystroke.
 *
 * Content only. Not one setting is touched, because every setting is the
 * merchant's answer to a question they have already been asked, and a stand-in
 * that overrode one would be showing them a page they did not design.
 */
export function withPlaceholderContent(section: Section): Section {
  switch (section.type) {
    case 'hero': {
      // Only the picture: the heading is required by the schema and the
      // renderer already falls back to the shop's own name.
      if (heroImages(section).length > 0) return section;
      return {
        ...section,
        images: [
          {
            id: placeholderId('hero-image', 0),
            image_url: placeholderImage('lifestyle', 0),
            image_focal: null,
            visible: true,
          },
        ],
      };
    }

    case 'slideshow': {
      const blocks = blankBlocks(
        section.blocks,
        SLIDES.length,
        (index) => ({
          id: placeholderId('slide', index),
          visible: true,
          image_url: placeholderImage('lifestyle', index + 1),
          image_focal: null,
          heading: SLIDES[index % SLIDES.length].heading,
          subheading: SLIDES[index % SLIDES.length].subheading,
          cta_label: 'Shop now',
          cta_href: '',
        }),
        // A slide the merchant has written but not illustrated. The words are
        // theirs and stay theirs; only the picture behind them is stood in for.
        (block, index) =>
          block.image_url ? block : { ...block, image_url: placeholderImage('lifestyle', index + 1) },
      );
      return blocks ? { ...section, blocks } : section;
    }

    case 'image_with_text':
      if (section.image_url) return section;
      return { ...section, image_url: placeholderImage('lifestyle', 2) };

    case 'gallery': {
      const blocks = blankBlocks(
        section.blocks,
        fill(section.columns, 2),
        (index) => ({
          id: placeholderId('gallery', index),
          visible: true,
          image_url: placeholderImage(index % 3 === 1 ? 'portrait' : 'lifestyle', index),
          image_focal: null,
          caption: GALLERY_CAPTIONS[index % GALLERY_CAPTIONS.length],
          href: '',
        }),
        (block, index) =>
          block.image_url
            ? block
            : {
                ...block,
                image_url: placeholderImage(index % 3 === 1 ? 'portrait' : 'lifestyle', index),
              },
      );
      return blocks ? { ...section, blocks } : section;
    }

    case 'testimonials': {
      const blocks = blankBlocks(
        section.blocks,
        fill(section.columns),
        (index) => ({
          id: placeholderId('testimonial', index),
          visible: true,
          rating: 5,
          ...TESTIMONIALS[index % TESTIMONIALS.length],
        }),
        (block, index) =>
          block.quote.trim()
            ? block
            : { ...block, rating: block.rating ?? 5, ...TESTIMONIALS[index % TESTIMONIALS.length] },
      );
      return blocks ? { ...section, blocks } : section;
    }

    case 'faq': {
      const blocks = blankBlocks(
        section.blocks,
        QUESTIONS.length,
        (index) => ({
          id: placeholderId('question', index),
          visible: true,
          ...QUESTIONS[index % QUESTIONS.length],
        }),
        // A question with no answer under it is a design; a row with no question
        // on it is not drawn at all, and that is the one worth standing in for.
        (block, index) =>
          block.question.trim() ? block : { ...block, ...QUESTIONS[index % QUESTIONS.length] },
      );
      if (!blocks) return section;
      // Invented stand-ins carry invented ids, which a section pinned to
      // catalog questions (`faq_ids`) would filter straight back out - so the
      // pin goes with them. Filled-in-place blocks keep their ids, and the pin.
      return section.blocks.length === 0
        ? { ...section, blocks, faq_ids: [] }
        : { ...section, blocks };
    }

    case 'features': {
      const stats = section.style === 'stats';
      const blocks = blankBlocks(
        section.blocks,
        fill(section.columns),
        (index) => ({
          id: placeholderId('promise', index),
          visible: true,
          ...(stats
            ? STATS[index % STATS.length]
            : { ...PROMISES[index % PROMISES.length], stat: '' }),
        }),
        // A promise arrives with an icon and a title already on it, and one
        // stated in three words with no line under it is a design rather than
        // an omission. A figure is different: the stats style without one is
        // a row of titles with a hole above each, so that is stood in for.
        (block, index) =>
          stats && !block.stat.trim() ? { ...block, stat: STATS[index % STATS.length].stat } : block,
      );
      return blocks ? { ...section, blocks } : section;
    }

    case 'timeline': {
      const blocks = blankBlocks(
        section.blocks,
        MILESTONES.length,
        (index) => ({
          id: placeholderId('milestone', index),
          visible: true,
          image_url: '',
          ...MILESTONES[index % MILESTONES.length],
        }),
        // A step with no title is not drawn; a step with no words under it is.
        (block, index) =>
          block.title.trim() ? block : { ...block, ...MILESTONES[index % MILESTONES.length] },
      );
      return blocks ? { ...section, blocks } : section;
    }

    case 'bookings': {
      const blocks = blankBlocks(
        section.blocks,
        fill(section.columns),
        (index) => {
          const service = SERVICES[index % SERVICES.length];
          return {
            id: placeholderId('service', index),
            visible: true,
            name: service.name,
            description: service.description,
            duration_minutes: service.duration,
            price: service.price,
            image_url: '',
            image_focal: null,
            location: 'place' as const,
            location_note: '',
          };
        },
        // As with a promise: a service arrives named, and a price list without
        // descriptions is a price list rather than a half-finished one.
        (block) => block,
      );
      return blocks ? { ...section, blocks } : section;
    }

    case 'text':
      if (section.body.trim()) return section;
      return { ...section, body: RICH_TEXT };

    // Typed-in groups only: from categories it draws on the placeholder
    // categories filled in where the section list is assembled.
    case 'link_directory':
      if (section.source !== 'manual' || section.blocks.some((block) => block.visible)) return section;
      return {
        ...section,
        blocks: [0, 1, 2].map((index) => ({
          id: placeholderId('link-group', index),
          visible: true,
          heading: 'Example group',
          links: [1, 2, 3, 4].map((n) => ({ label: `Example link ${n}`, href: '/products' })),
        })),
      };

    case 'banner':
      if (section.text.trim()) return section;
      return {
        ...section,
        text: 'Your announcement - a sale, a delivery cut-off, a new arrival.',
        link_label: section.link_label || 'Find out more',
      };

    default:
      // Everything else - the product grids, the category grid, the forms and
      // every anchor - either draws from the catalog, which is filled by
      // `placeholderProducts` where the section list is assembled, or has
      // nothing missing to fill.
      return section;
  }
}
